
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageData, setMessageData] = useState({ subject: '', body: '' });
  const [error, setError] = useState('');
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  useEffect(() => {
    fetchListingDetail();
  }, [id]);

  const fetchListingDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/listings/${id}`);

      if (response.data.success) {
        setListing(response.data.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching listing:', error);
      setError('Listing not found');
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    try {
      await api.post('/api/messages', {
        recipientId: listing.seller._id,
        subject: messageData.subject,
        body: messageData.body,
        listingId: id
      });

      alert('Message sent successfully! The seller will receive an email.');
      setShowMessageForm(false);
      setMessageData({ subject: '', body: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error.response?.data?.message || 'Failed to send message');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-300 rounded w-1/2"></div>
          <div className="h-96 bg-gray-300 rounded"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-300 rounded w-full"></div>
            <div className="h-4 bg-gray-300 rounded w-5/6"></div>
            <div className="h-4 bg-gray-300 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-4">{error}</h1>
        <Link to="/listings" className="bg-primary text-white px-6 py-2 rounded-md hover:bg-blue-800 transition-colors">
          Back to Listings
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="text-sm mb-6">
        <ol className="flex space-x-2 text-gray-500">
          <li><Link to="/" className="hover:text-primary">Home</Link></li>
          <li>/</li>
          <li><Link to="/listings" className="hover:text-primary">Listings</Link></li>
          <li>/</li>
          <li className="text-gray-800 font-medium">{listing.title}</li>
        </ol>
      </nav>

      {/* Title, Seller, Location, Price */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">{listing.title}</h1>
        <div className="flex flex-wrap items-center gap-4 mb-4 text-gray-600">
          <span className="flex items-center gap-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Listed by <span className="font-semibold text-primary ml-1">{listing.seller?.username}</span>
          </span>
          <span className="flex items-center gap-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {listing.location?.city && listing.location?.state
              ? `${listing.location.city}, ${listing.location.state}`
              : 'Location not specified'}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-3xl font-bold text-secondary">${listing.price?.toLocaleString() || 'Free'}</span>
          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            {listing.condition}
          </span>
        </div>
      </div>

      {/* Images */}
      <div className="mb-8">
        {listing.images && listing.images.length > 0 && listing.images.some(img => img.url) ? (
          <>
            {/* Main Image */}
            <div className="relative bg-gray-900 rounded-lg overflow-hidden mb-4">
              <img
                src={selectedImageIndex !== null
                  ? `http://localhost:5000${listing.images[selectedImageIndex].url}`
                  : `http://localhost:5000${listing.images[0].url}`
                }
                alt={`${listing.title} - View ${selectedImageIndex !== null ? selectedImageIndex + 1 : 1}`}
                className="w-full max-h-[600px] object-contain mx-auto"
              />
              {listing.images.length > 1 && (
                <>
                  <button
                    onClick={() => setSelectedImageIndex(
                      selectedImageIndex !== null
                        ? (selectedImageIndex - 1 + listing.images.length) % listing.images.length
                        : 0
                    )}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setSelectedImageIndex(
                      selectedImageIndex !== null
                        ? (selectedImageIndex + 1) % listing.images.length
                        : 0
                    )}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <span className="absolute top-2 left-2 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                    {selectedImageIndex !== null ? selectedImageIndex + 1 : 1} / {listing.images.length}
                  </span>
                </>
              )}
            </div>
            {/* Thumbnail strip */}
            {listing.images.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto pb-2">
                {listing.images.filter(img => img.url).map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImageIndex === index
                        ? 'border-primary'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={img.thumbnailUrl || `http://localhost:5000${img.url}`}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="bg-gray-200 h-96 flex items-center justify-center text-gray-400 rounded-lg">
            No images uploaded for this listing
          </div>
        )}
      </div>

      {/* Description */}
      <div className="mb-8 bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Description</h2>
        <p className="text-gray-700 leading-relaxed">{listing.description}</p>
      </div>

      {/* Message Interface */}
      {user && user.userId !== listing.seller?._id ? (
        <div className="mb-8 bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Contact {listing.seller?.username}</h2>
          <button
            onClick={() => setShowMessageForm(!showMessageForm)}
            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-blue-800 transition-colors"
          >
            {showMessageForm ? 'Cancel' : 'Send Message'}
          </button>

          {showMessageForm && (
            <form onSubmit={handleSendMessage} className="mt-4 space-y-4">
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded" role="alert">
                  <strong className="font-bold">Error! </strong>
                  <span className="block sm:inline">{error}</span>
                </div>
              )}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  id="subject"
                  value={messageData.subject}
                  onChange={(e) => setMessageData({ ...messageData, subject: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder={`About "${listing.title}"`}
                />
              </div>
              <div>
                <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  id="body"
                  value={messageData.body}
                  onChange={(e) => setMessageData({ ...messageData, body: e.target.value })}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                  placeholder="Write your message here..."
                ></textarea>
              </div>
              <button type="submit" className="bg-secondary text-white px-6 py-2 rounded-md hover:bg-amber-600 transition-colors">
                Send Message
              </button>
              <p className="text-xs text-gray-500">
                The seller will receive an email notification. Your email address remains private.
              </p>
            </form>
          )}
        </div>
      ) : user && user.userId === listing.seller?._id ? (
        <div className="mb-8 bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg shadow-md">
          <p className="text-gray-700">You are the seller of this listing. You cannot message yourself.</p>
        </div>
      ) : null}

      {/* Back Button */}
      <div className="mt-8">
        <Link to="/listings" className="inline-block bg-gray-200 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-300 transition-colors">
          ← Back to Listings
        </Link>
      </div>
    </div>
  );
}

export default ListingDetail;
